/**
 * Convert between the API's snake_case JSON and the frontend's camelCase.
 *
 * Keys are recursively transformed; values, arrays and primitives are
 * preserved. This lets the rest of the app speak camelCase exclusively.
 */

const snakeToCamel = (key: string): string =>
  key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());

const camelToSnake = (key: string): string =>
  key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  (value as object).constructor === Object;

const mapKeys = (
  value: unknown,
  transform: (key: string) => string,
): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => mapKeys(item, transform));
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        transform(key),
        mapKeys(val, transform),
      ]),
    );
  }

  return value;
};

export const keysToCamel = <T>(value: unknown): T =>
  mapKeys(value, snakeToCamel) as T;

export const keysToSnake = (value: unknown): unknown =>
  mapKeys(value, camelToSnake);
